import type { ReactNode } from 'react'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'

import { RestrictToCapability } from '../../../components/RestrictToCapability'
import { useUserDropdownMenu } from '../../../components/UserDropdownMenuContext'
import { useProposeChangeDialog } from '../hooks/useUserFeedbackDialog'
import { ProposeChangeDialog } from './ProposeChangeDialog'

interface ProposeChangeState {
  isDialogOpen: boolean
}

interface ProposeChangeActions {
  closeProposeChangeDialog: () => void
  openProposeChangeDialog: () => void
}

type ProposeChangeContextValue = CompositionContextValue<
  ProposeChangeState,
  ProposeChangeActions
>

const ProposeChangeContext =
  createNonNullableContext<ProposeChangeContextValue>('ProposeChangeContext')

export const useProposeChangeContext = ProposeChangeContext.useRequiredContext

export interface ProposeChangeProviderProps {
  readonly children: ReactNode
}

export function ProposeChangeProvider({
  children,
}: ProposeChangeProviderProps) {
  const proposeChangeDialog = useProposeChangeDialog()
  const userDropdownMenu = useUserDropdownMenu()

  const closeProposeChangeDialog = () => {
    userDropdownMenu.actions.setUserDropdownMenuOpen(false)
    proposeChangeDialog.closeFeedbackDialog()
  }

  return (
    <ProposeChangeContext.Context
      value={{
        actions: {
          closeProposeChangeDialog,
          openProposeChangeDialog: proposeChangeDialog.openFeedbackDialog,
        },
        state: {
          isDialogOpen: proposeChangeDialog.isDialogOpen,
        },
      }}
    >
      {children}
      <RestrictToCapability capability="product_change.propose">
        <ProposeChangeDialog
          proposeChangeDialog={{
            ...proposeChangeDialog,
            closeFeedbackDialog: closeProposeChangeDialog,
          }}
        />
      </RestrictToCapability>
    </ProposeChangeContext.Context>
  )
}
